import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { EmptyState } from '@/components/empty-state'
import { Pager } from '@/components/pager'
import { Panel } from '@/components/panel'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ACTION_DIMENSION_LABELS,
  ACTION_DIMENSIONS,
  type ActionDimension,
  type ActionFilters,
  type AdminActionRow,
  DIMENSION_FILTER,
  useAdminActions,
} from '@/lib/admin-actions'
import { formatCount, formatExact } from '@/lib/format'
import { cn } from '@/lib/utils'
import { Age, ClearFilters, FilterSelect, Segmented, SkeletonRows, Tag, Th } from '../../-parts'

const PAGE_SIZE = 50

interface ActionSearch extends ActionFilters {
  /** Which way the counts are cut, independent of what is filtered. */
  cut?: ActionDimension
  offset?: number
}

function parseCut(value: unknown): ActionDimension | undefined {
  return typeof value === 'string' && (ACTION_DIMENSIONS as readonly string[]).includes(value)
    ? (value as ActionDimension)
    : undefined
}

function parseText(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

export const Route = createFileRoute('/_app/admin/actions/_list/')({
  // Every control lands in the URL, for the reason the cost filters do: a link
  // to a finding has to mean the same thing for whoever opens it.
  validateSearch: (search: Record<string, unknown>): ActionSearch => {
    const next: ActionSearch = {}
    const cut = parseCut(search.cut)
    if (cut) next.cut = cut
    for (const key of ['type', 'state', 'nature', 'organizationId', 'siteId'] as const) {
      const value = parseText(search[key])
      if (value) next[key] = value
    }
    // Narrowed rather than carried as text: the api takes an enum here, and a
    // stale link with a third value should read as no filter rather than as a
    // 422 from a query string somebody bookmarked.
    const suppression = parseText(search.suppression)
    if (suppression === 'shown' || suppression === 'suppressed') next.suppression = suppression
    const offset = Number(search.offset)
    if (Number.isFinite(offset) && offset > 0) next.offset = Math.floor(offset)
    return next
  },
  component: ActionsPage,
})

const CUTS = ACTION_DIMENSIONS.map((dimension) => ({
  value: dimension,
  label: ACTION_DIMENSION_LABELS[dimension],
}))

/**
 * Every action on the deployment.
 *
 * The suppression cut is the one a customer has no equivalent of: the rows the
 * model vetoed never reach their list, so a detector whose threshold is too
 * loose is invisible everywhere but here. Cutting and filtering stay
 * independent controls — "which check is this workspace's list made of" is a
 * routine question.
 */
function ActionsPage() {
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { cut = 'type', offset = 0 } = search

  // Named rather than rest-spread off the search: the window the top bar keeps
  // is on the parent route and arrives in here too, and it is not a filter.
  const filters: ActionFilters = {
    type: search.type,
    state: search.state,
    nature: search.nature,
    organizationId: search.organizationId,
    siteId: search.siteId,
    suppression: search.suppression,
  }

  const { data, isPending } = useAdminActions(filters, PAGE_SIZE, offset)

  const set = (next: Partial<ActionSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...next, offset: undefined }), replace: true })

  const filtered = Object.values(filters).some(Boolean)
  const facets = data?.facets
  const breakdown = data?.breakdown[cut] ?? []
  const widest = breakdown.reduce((most, row) => Math.max(most, row.actions), 0)
  const active = filters[DIMENSION_FILTER[cut]]

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterSelect
          all="All checks"
          options={facets?.type ?? []}
          value={filters.type}
          format={(option) => option.label}
          onChange={(next) => set({ type: next })}
        />
        <FilterSelect
          all="Any state"
          options={facets?.state ?? []}
          value={filters.state}
          onChange={(next) => set({ state: next })}
        />
        <FilterSelect
          all="Any nature"
          options={facets?.nature ?? []}
          value={filters.nature}
          onChange={(next) => set({ nature: next })}
        />
        <FilterSelect
          all="All workspaces"
          options={facets?.organization ?? []}
          value={filters.organizationId}
          onChange={(next) => set({ organizationId: next })}
        />
        <FilterSelect
          all="All sites"
          options={facets?.site ?? []}
          value={filters.siteId}
          onChange={(next) => set({ siteId: next })}
        />
        <FilterSelect
          all="Shown and suppressed"
          options={facets?.suppression ?? []}
          value={filters.suppression}
          onChange={(next) =>
            set({ suppression: next === 'suppressed' || next === 'shown' ? next : undefined })
          }
        />

        {filtered && (
          <ClearFilters
            onClear={() =>
              set({
                type: undefined,
                state: undefined,
                nature: undefined,
                organizationId: undefined,
                siteId: undefined,
                suppression: undefined,
              })
            }
          />
        )}
      </div>

      <div className="space-y-4">
        <Panel
          title={`By ${ACTION_DIMENSION_LABELS[cut].toLowerCase()}`}
          actions={<Segmented value={cut} options={CUTS} onChange={(next) => set({ cut: next })} />}
        >
          {isPending ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {breakdown.map((row) => (
                <button
                  key={row.key}
                  type="button"
                  aria-pressed={active === row.key}
                  onClick={() =>
                    set({
                      [DIMENSION_FILTER[cut]]: active === row.key ? undefined : row.key,
                    } as Partial<ActionSearch>)
                  }
                  className={cn(
                    'group min-w-[140px] cursor-pointer rounded-md border px-2.5 py-1.5 text-left transition-colors',
                    active === row.key
                      ? 'border-border-strong bg-plum-selected'
                      : 'border-border hover:border-border-strong',
                  )}
                >
                  <span className="flex items-baseline gap-3">
                    <span className="min-w-0 flex-1 truncate font-mono text-[12px]">
                      {row.label || row.key}
                    </span>
                    <span className="shrink-0 font-mono text-[12px] tabular">
                      {formatExact(row.actions)}
                    </span>
                  </span>
                  <span
                    className="mt-1.5 block h-[3px] overflow-hidden rounded-[1px] bg-border"
                    aria-hidden
                  >
                    <span
                      className="block h-full rounded-[1px] bg-foreground/60"
                      style={{ width: `${widest === 0 ? 0 : (row.actions / widest) * 100}%` }}
                    />
                  </span>
                </button>
              ))}
              {breakdown.length === 0 && (
                <p className="py-2 text-[13px] text-muted-foreground">
                  Nothing matches those filters.
                </p>
              )}
            </div>
          )}
        </Panel>

        <Panel title="Actions" meta={data ? formatExact(data.total) : undefined} flush>
          {isPending ? (
            <SkeletonRows rows={6} />
          ) : data && data.actions.length === 0 ? (
            <EmptyState
              title="No actions"
              description="Nothing matches those filters. A check that has never run somewhere reads the same way here, which is what the detectors screen is for."
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr className="border-b border-border bg-secondary/30">
                      <Th align="left">Action</Th>
                      <Th align="left" className="w-[170px]">
                        Check
                      </Th>
                      <Th align="left" className="w-[150px]">
                        State
                      </Th>
                      <Th align="left" className="w-[180px]">
                        Site
                      </Th>
                      <Th className="w-[90px]">Clicks</Th>
                      <Th className="w-[80px]">Priority</Th>
                      <Th className="w-[70px]">Seen</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.actions.map((row) => (
                      <Row key={row.id} row={row} />
                    ))}
                  </tbody>
                </table>
              </div>

              {data && data.total > PAGE_SIZE && (
                <div className="flex justify-end border-t border-border px-4 py-2">
                  <Pager
                    offset={offset}
                    limit={PAGE_SIZE}
                    total={data.total}
                    onOffsetChange={(next) =>
                      navigate({ search: (prev) => ({ ...prev, offset: next }), replace: true })
                    }
                  />
                </div>
              )}
            </>
          )}
        </Panel>
      </div>
    </>
  )
}

function Row({ row }: { row: AdminActionRow }) {
  const navigate = useNavigate()
  const now = Date.now()

  return (
    <tr
      className="cursor-pointer border-b border-border last:border-b-0 hover:bg-secondary/40"
      onClick={() => navigate({ to: '/admin/actions/$actionId', params: { actionId: row.id } })}
    >
      <td className="max-w-[380px] py-2 pr-3 pl-3">
        <Link
          to="/admin/actions/$actionId"
          params={{ actionId: row.id }}
          onClick={(event) => event.stopPropagation()}
          className={cn(
            'block truncate hover:text-link',
            row.suppressed && 'text-muted-foreground',
          )}
        >
          {row.headline ?? row.title}
        </Link>
        {/* The veto reason where there is one, because that is the whole of why
            this row exists on a staff screen and on no customer's. */}
        <span className="block truncate font-mono text-[11px] text-muted-foreground">
          {row.skippedReason ?? `${row.subjectKind} · ${row.subjectKey}`}
        </span>
      </td>

      <td className="px-3 py-2">
        <span className="block max-w-[160px] truncate font-mono text-[11.5px] text-muted-foreground">
          {row.type}
        </span>
      </td>

      <td className="px-3 py-2">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-[12px]">{row.state}</span>
          <Tag>{row.nature}</Tag>
          {row.suppressed && <Tag tone="warn">suppressed</Tag>}
        </span>
      </td>

      <td className="px-3 py-2">
        <span className="block max-w-[170px] truncate font-mono text-[12px]">{row.host}</span>
        <span className="block max-w-[170px] truncate text-[11px] text-muted-foreground">
          {row.organization}
        </span>
      </td>

      <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
        {/* A value the site had no benchmark to estimate against is not zero,
            so the cell stays empty rather than claiming one. */}
        {row.valueClicks === null ? '' : formatCount(Math.round(row.valueClicks))}
      </td>

      <td className="px-3 py-2 text-right font-mono text-[12px] text-muted-foreground tabular">
        {Math.round(row.priority)}
      </td>

      <td className="px-3 py-2 text-right">
        <Age at={row.lastSeenAt} now={now} />
      </td>
    </tr>
  )
}
